import { FC } from 'react';
import {
    Box,
    CircularProgress,
    Alert,
    Grid,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { KpiCard } from '../common/KpiCard';
import { StatsBarChart } from '../common/StatsBarChart';
import { useLearningSummary } from '../../../hooks/analytics/useLearning';
import { MasteryBuckets } from '../../../types/analytics';

interface Props {
    startDate: string;
    endDate: string;
}

const MASTERY_COLORS: Record<string, string> = {
    new: '#e3f2fd',
    learning: '#fff9c4',
    review: '#c8e6c9',
    mastered: '#4caf50',
};

const MASTERY_LABELS: Record<string, string> = {
    new: 'NEW',
    learning: 'LEARNING',
    review: 'REVIEW',
    mastered: 'MASTERED',
};

const WORDBOOK_QUIZ_PREFIX = 'WORDBOOK_';

function stripPrefix(quizType: string): string {
    if (quizType.startsWith(WORDBOOK_QUIZ_PREFIX)) return quizType.slice(WORDBOOK_QUIZ_PREFIX.length);
    return quizType;
}

function MasteryBar({ buckets }: { buckets: MasteryBuckets }) {
    const total = buckets.new + buckets.learning + buckets.review + buckets.mastered;
    if (total === 0) {
        return (
            <Typography variant="body2" color="text.secondary" align="center">
                데이터 없음
            </Typography>
        );
    }

    const items = [
        { key: 'new', value: buckets.new },
        { key: 'learning', value: buckets.learning },
        { key: 'review', value: buckets.review },
        { key: 'mastered', value: buckets.mastered },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', height: 32, borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                {items.map((item) => {
                    const pct = (item.value / total) * 100;
                    if (pct === 0) return null;
                    return (
                        <Box
                            key={item.key}
                            sx={{
                                width: `${pct}%`,
                                bgcolor: MASTERY_COLORS[item.key],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: pct > 5 ? 'auto' : 0,
                            }}
                        >
                            {pct > 8 && (
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                                    {pct.toFixed(0)}%
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {items.map((item) => (
                    <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: MASTERY_COLORS[item.key], border: item.key === 'new' ? '1px solid #90caf9' : 'none' }} />
                        <Typography variant="caption" color="text.secondary">
                            {MASTERY_LABELS[item.key]}: {item.value.toLocaleString()}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

const LearningSummaryTab: FC<Props> = ({ startDate, endDate }) => {
    const { data, isLoading, isError, error } = useLearningSummary(startDate, endDate);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {(error as Error)?.message ?? '데이터를 불러오지 못했습니다.'}
            </Alert>
        );
    }

    if (!data) return null;

    const kpi = data.kpi;
    const sessionTrend = data.sessionTrend ?? [];
    const quizAccuracy = data.quizAccuracy ?? [];
    const mastery = data.masteryDistribution ?? {
        wordbook: { new: 0, learning: 0, review: 0, mastered: 0 },
    };
    const topLibraries = data.topLibraries ?? [];

    const wordbookQuizAccuracy = quizAccuracy.filter((q) => q.quizType.startsWith(WORDBOOK_QUIZ_PREFIX));

    return (
        <>
            {/* KPI Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <KpiCard
                        title="총 학습 세션"
                        value={(kpi?.totalSessions ?? 0).toLocaleString()}
                        changePercent={kpi?.sessionChange}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <KpiCard
                        title="일평균 학습자"
                        value={(kpi?.avgDailyLearners ?? 0).toFixed(0)}
                        changePercent={kpi?.learnerChange}
                        color="green"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <KpiCard
                        title="평균 세션 시간"
                        value={`${(kpi?.avgSessionMinutes ?? 0).toFixed(1)}분`}
                        changePercent={kpi?.minuteChange}
                        color="orange"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <KpiCard
                        title="전체 정답률"
                        value={`${(kpi?.overallAccuracy ?? 0).toFixed(1)}%`}
                        changePercent={kpi?.accuracyChange}
                        color="purple"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <KpiCard
                        title="학습 완료 단어"
                        value={(kpi?.learnedWords ?? 0).toLocaleString()}
                        subtitle="고유 단어 기준 (단어장 중복 제외)"
                        changePercent={kpi?.learnedChange}
                        color="green"
                    />
                </Grid>
            </Grid>

            {/* Charts: Session Trend */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            일별 학습 세션 추이
                        </Typography>
                        <StatsBarChart
                            data={sessionTrend as unknown as Record<string, unknown>[]}
                            series={[
                                { dataKey: 'wordbook', name: '단어장', color: '#ef6c00', stackId: 's' },
                            ]}
                            xAxisKey="date"
                        />
                    </Paper>
                </Grid>
            </Grid>

            {/* Analysis: Quiz Accuracy + Mastery Distribution */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            퀴즈 유형별 정답률
                        </Typography>

                        {wordbookQuizAccuracy.length > 0 && (
                            <>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                                    단어장
                                </Typography>
                                {wordbookQuizAccuracy.map((q) => (
                                    <Box key={q.quizType} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                                        <Typography variant="body2" sx={{ width: 160, flexShrink: 0, fontSize: '0.8rem' }}>
                                            {stripPrefix(q.quizType)}
                                        </Typography>
                                        <Box sx={{ flex: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(q.accuracy, 100)}
                                                sx={{
                                                    height: 10,
                                                    borderRadius: 5,
                                                    bgcolor: '#f1f5f9',
                                                    '& .MuiLinearProgress-bar': { bgcolor: '#ef6c00', borderRadius: 5 },
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="body2" sx={{ width: 50, textAlign: 'right', fontWeight: 600 }}>
                                            {q.accuracy.toFixed(1)}%
                                        </Typography>
                                    </Box>
                                ))}
                            </>
                        )}

                        {quizAccuracy.length === 0 && (
                            <Typography variant="body2" color="text.secondary" align="center">
                                데이터 없음
                            </Typography>
                        )}
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="subtitle2">
                                단어 숙달 단계 분포
                            </Typography>
                            <Tooltip
                                title="MASTERED = 복습에서 다시 맞힌 단어 (2026-06 마스터 시스템). 단어 마스터 시스템의 clean start로 당분간 MASTERED 수치가 0에 가깝고 대신 REVIEW가 늘어납니다 — 새 정의(복습 완료 = 마스터)에 따른 의도된 동작입니다."
                                arrow
                            >
                                <InfoOutlinedIcon
                                    sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'help' }}
                                />
                            </Tooltip>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            단어장
                        </Typography>
                        <MasteryBar buckets={mastery.wordbook} />
                    </Paper>
                </Grid>
            </Grid>

            {/* Table: Top Libraries */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            인기 라이브러리
                        </Typography>
                        <TableContainer sx={{ maxHeight: 360 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>라이브러리</TableCell>
                                        <TableCell align="right">다운로드</TableCell>
                                        <TableCell align="right">평점</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {topLibraries.map((row, idx) => (
                                        <TableRow key={row.title}>
                                            <TableCell>{idx + 1}</TableCell>
                                            <TableCell>{row.title}</TableCell>
                                            <TableCell align="right">{row.downloads.toLocaleString()}</TableCell>
                                            <TableCell align="right">
                                                {row.ratingAverage.toFixed(1)} ({row.ratingCount})
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {topLibraries.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                데이터 없음
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </>
    );
};

export default LearningSummaryTab;
